import { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { BRAND, NEON_VERSE_STUDIO, UK_BUILDERS } from '../constants/brand';

type Testimonial = {
  quote: string;
  name: string;
  role: string;
  projectUrl?: string;
  projectLabel?: string;
};

const testimonials: Testimonial[] = [
  {
    quote: `${BRAND.name} helped us grow digitally in our domain—hospitality and ground-up construction. Our new site tells our story with credibility; we’re reaching better-fit partners and seeing stronger engagement online.`,
    name: UK_BUILDERS.ownerName,
    role: `${UK_BUILDERS.roleTitle}, ${UK_BUILDERS.company} — ${UK_BUILDERS.region}`,
    projectUrl: 'https://skaarapps.com/ukbuilders',
    projectLabel: 'View live site',
  },
  {
    quote: `${BRAND.name} brought Neon Verse Studio online with a site that feels as personal as the songs we create. From story to launch, the experience matched our brand—and we’re ready to reach more people who want music made for their moments.`,
    name: NEON_VERSE_STUDIO.ownerName,
    role: `${NEON_VERSE_STUDIO.roleTitle}, ${NEON_VERSE_STUDIO.company} — ${NEON_VERSE_STUDIO.region}`,
    projectUrl: 'https://skaarapps.com/neonverse',
    projectLabel: 'View live site',
  },
  {
    quote: `${BRAND.name} helped us modernize our infrastructure without disrupting day-to-day operations. The roadmap was clear, and delivery matched what we were promised.`,
    name: 'Jordan Ellis',
    role: 'VP of Operations, Regional Healthcare Network',
  },
  {
    quote: `We finally have a security posture we can explain to our board. ${BRAND.name} made identity, monitoring, and incident playbooks something our team actually uses.`,
    name: 'Priya Nair',
    role: 'CISO, Financial Services Firm',
  },
  {
    quote: `From discovery to handoff, it felt like a partnership—not a ticket queue. Our internal IT load dropped measurably within the first quarter.`,
    name: 'Marcus Webb',
    role: 'IT Director, Manufacturing',
  },
];

function TestimonialCard({ t }: { t: Testimonial }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-card p-6 md:p-8 flex flex-col justify-center h-full">
      <blockquote className="border-l-4 border-brand-violet/35 pl-4 md:pl-6 text-slate-700 text-base md:text-lg leading-relaxed mb-6 flex-1">
        {t.quote}
      </blockquote>
      <footer className="mt-auto">
        <p className="font-semibold text-slate-900">{t.name}</p>
        <p className="text-sm text-slate-600 mt-1">{t.role}</p>
        {t.projectUrl && t.projectLabel && (
          <a
            href={t.projectUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-3 text-sm font-medium text-brand-violet hover:underline"
          >
            {t.projectLabel}
          </a>
        )}
      </footer>
    </div>
  );
}

const Testimonials = () => {
  const [startIndex, setStartIndex] = useState(0);
  const n = testimonials.length;

  const goPrev = useCallback(() => {
    setStartIndex((i) => (i - 1 + n) % n);
  }, [n]);

  const goNext = useCallback(() => {
    setStartIndex((i) => (i + 1) % n);
  }, [n]);

  const visible = [0, 1].map((offset) => testimonials[(startIndex + offset) % n]);

  return (
    <section id="testimonials" className="py-20 md:py-28 border-y border-slate-200 bg-surface-elevated scroll-mt-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            What clients <span className="text-gradient">say</span>
          </h2>
          <p className="text-slate-600 text-lg">
            Real feedback from teams we have partnered with—navigate with the arrows.
          </p>
        </motion.div>

        <div className="flex items-stretch gap-3 md:gap-5 max-w-6xl mx-auto">
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous testimonials"
            className="shrink-0 self-center flex h-11 w-11 md:h-12 md:w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:border-brand-violet/40 hover:text-brand-violet transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet/50"
          >
            <FiChevronLeft className="h-6 w-6" aria-hidden />
          </button>

          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={startIndex}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
              >
                {visible.map((t, i) => (
                  <TestimonialCard key={`${startIndex}-${i}`} t={t} />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          <button
            type="button"
            onClick={goNext}
            aria-label="Next testimonials"
            className="shrink-0 self-center flex h-11 w-11 md:h-12 md:w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:border-brand-violet/40 hover:text-brand-violet transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet/50"
          >
            <FiChevronRight className="h-6 w-6" aria-hidden />
          </button>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6" aria-live="polite">
          {startIndex + 1} / {n}
        </p>
      </div>
    </section>
  );
};

export default Testimonials;
